import {
  useEffect,
  useContext,
  useState,
  useCallback,
  useRef
} from 'react'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'

import { AppContext } from '../components/AppContext.tsx'
import PageLayout from '../components/PageLayout.jsx'
import SettingsRow from '../components/SettingsRow.jsx'
import SaveBtn from '../components/microComponents/SaveBtn.jsx'
import ValidatorIcon from '../components/microComponents/ValidatorIcon.jsx'
import InfoIcon from '../components/icons/infocircle.svg?react'
import ModelPreferences from '../components/usage/ModelPreferences.jsx'
import SettingsInput from '../components/SettingsInput.jsx'
import { Button } from '../components/primitives/Button.jsx'
import { ActionRow } from '../components/ActionRow.jsx'

import { apiClient } from '../lib/apiClient'
import { appStorage } from '../lib/appStorage'

const SettingsPage = () => {
  const navigate = useNavigate()
  const {
    authSession,
    apiKeys,
    updateApiKeys,
    deleteAllConversations,
    profile,
    updateProfile,
    showConfirmation,
    // UI state
    initializingUI,
    // Models
    models,
    enabledModelIds: contextEnabledModelIds,
    favoriteModelId: contextFavoriteModelId,
    setEnabledModelIds: updateContextEnabledModelIds,
    setFavoriteModelId: updateContextFavoriteModelId,
    preferAnthropicWebSearch,
    setPreferAnthropicWebSearch,
  } = useContext(AppContext)

  const [anthropicApiKey, setAnthropicApiKey] = useState("")
  const [openAIApiKey, setOpenAIApiKey] = useState("")
  const [geminiApiKey, setGeminiApiKey] = useState("")
  const [xaiApiKey, setXaiApiKey] = useState("")
  const [openrouterApiKey, setOpenrouterApiKey] = useState("")
  const [firstName, setFirstName] = useState(() => profile ? profile.firstname : "")
  const [lastName, setLastName] = useState(() => profile ? profile.lastname : "")
  const [email, setEmail] = useState(() => profile ? profile.email : "")
  const [savingProfile, setSavingProfile] = useState(false)
  const [savingApiKeys, setSavingApiKeys] = useState(false)
  const [preferAnthropicWebSearchLocal, setPreferAnthropicWebSearchLocal] = useState(preferAnthropicWebSearch)

  // Agent tools state (Electron only)
  const isElectron = typeof window !== 'undefined' && !!window.electronAPI?.isElectron
  const [workingDirectory, setWorkingDirectory] = useState('')

  useEffect(() => {
    if (!isElectron) return
    appStorage.getItem('workingDirectory').then(v => { if (v) setWorkingDirectory(v) })
  }, [isElectron])

  // Sync local web search preference with context
  useEffect(() => {
    setPreferAnthropicWebSearchLocal(preferAnthropicWebSearch)
  }, [preferAnthropicWebSearch])

  // Ref to hold latest API key values to avoid stale closures
  const apiKeyRef = useRef({
    openai: '',
    anthropic: '',
    gemini: '',
    xai: '',
    openrouter: ''
  })

  // Model preferences state
  const [localEnabledModelIds, setLocalEnabledModelIds] = useState([])
  const [localFavoriteModelId, setLocalFavoriteModelId] = useState(null)
  const [initialEnabledModelIds, setInitialEnabledModelIds] = useState([])
  const [initialFavoriteModelId, setInitialFavoriteModelId] = useState(null)

  // Validation states: 'idle' | 'validating' | 'valid' | 'invalid'
  const [validationStates, setValidationStates] = useState({
    openai: 'idle',
    anthropic: 'idle',
    gemini: 'idle',
    xai: 'idle',
    openrouter: 'idle'
  })

  // Track error messages from validation
  const [errorMessages, setErrorMessages] = useState({
    openai: '',
    anthropic: '',
    gemini: '',
    xai: '',
    openrouter: ''
  })

  // Track available models from validation
  const [availableModels, setAvailableModels] = useState({
    openai: 0,
    anthropic: 0,
    gemini: 0,
    xai: 0,
    openrouter: 0
  })

  // Preview models (from unsaved validated keys)
  const [previewModels, setPreviewModels] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  // Track which keys have been edited by the user
  const [dirtyKeys, setDirtyKeys] = useState({
    openai: false,
    anthropic: false,
    gemini: false,
    xai: false,
    openrouter: false
  })

  // Debounce timers
  const debounceTimers = useRef({})

  useEffect(() => {
    const seedKey = (key, provider, setter) => {
      const val = key || ''
      setter(val)
      apiKeyRef.current[provider] = val
      setDirtyKeys(prev => ({ ...prev, [provider]: false }))

      // Validate to show status, but don't trigger preview
      if (val) {
        handleKeyChange(val, provider, setter, { userInitiated: false })
      }
    }

    seedKey(apiKeys.openAIApiKey, 'openai', setOpenAIApiKey)
    seedKey(apiKeys.anthropicApiKey, 'anthropic', setAnthropicApiKey)
    seedKey(apiKeys.geminiApiKey, 'gemini', setGeminiApiKey)
    seedKey(apiKeys.xaiApiKey, 'xai', setXaiApiKey)
    seedKey(apiKeys.openrouterApiKey, 'openrouter', setOpenrouterApiKey)

  }, [apiKeys])

  useEffect(() => {
    setFirstName(profile?.firstname || "")
    setLastName(profile?.lastname || "")
    setEmail(profile?.email || "")
  }, [profile?.firstname, profile?.lastname, profile?.email])

  // Initialize model preferences from context
  useEffect(() => {
    if (models && models.length > 0) {
      const enabled = contextEnabledModelIds && contextEnabledModelIds.length > 0
        ? contextEnabledModelIds
        : models.map(m => m.id);

      setLocalEnabledModelIds(enabled);
      setInitialEnabledModelIds(enabled);
      setLocalFavoriteModelId(contextFavoriteModelId);
      setInitialFavoriteModelId(contextFavoriteModelId);
    }
  }, [models, contextEnabledModelIds, contextFavoriteModelId])

  // Validation function
  const validateApiKey = useCallback(async (key, provider) => {
    if (!authSession) return { is_valid: false, error_message: 'Not authenticated' }

    try {
      const result = await apiClient.post('/api/validate-api-key', {
        provider: provider,
        api_key: key
      })
      return result
    } catch (error) {
      console.error('API key validation error:', error)
      return { is_valid: false, error_message: 'Validation failed' }
    }
  }, [authSession])

  // Recompute preview models based on current validated keys
  const recomputePreviewModels = useCallback(async (currentValidationStates = null) => {
    if (!authSession) return

    const effectiveValidationStates = currentValidationStates || validationStates
    const keys = apiKeyRef.current
    const payload = {}

    // Only include keys that are dirty AND valid AND non-empty
    if (dirtyKeys.openai && keys.openai && effectiveValidationStates.openai === 'valid') {
      payload.openai_apikey = keys.openai
    }
    if (dirtyKeys.anthropic && keys.anthropic && effectiveValidationStates.anthropic === 'valid') {
      payload.anthropic_apikey = keys.anthropic
    }
    if (dirtyKeys.gemini && keys.gemini && effectiveValidationStates.gemini === 'valid') {
      payload.gemini_apikey = keys.gemini
    }
    if (dirtyKeys.xai && keys.xai && effectiveValidationStates.xai === 'valid') {
      payload.xai_apikey = keys.xai
    }
    if (dirtyKeys.openrouter && keys.openrouter && effectiveValidationStates.openrouter === 'valid') {
      payload.openrouter_apikey = keys.openrouter
    }

    // If nothing to preview (no dirty valid keys), clear preview and exit
    if (Object.keys(payload).length === 0) {
      setPreviewModels(null)
      return
    }

    setPreviewLoading(true)
    try {
      const data = await apiClient.post('/api/models/preview', payload)
      setPreviewModels(data)

      // Reconcile: only filter existing selection to those present in preview
      const previewIds = new Set(data.map(m => m.id))
      setLocalEnabledModelIds(prev => prev.filter(id => previewIds.has(id)))

      // Clear favorite if it's not in preview
      if (localFavoriteModelId && !previewIds.has(localFavoriteModelId)) {
        setLocalFavoriteModelId(null)
      }
    } catch (error) {
      console.error('Preview models error:', error)
    } finally {
      setPreviewLoading(false)
    }
  }, [
    authSession,
    validationStates,
    dirtyKeys,
    setLocalEnabledModelIds,
    localFavoriteModelId,
    setLocalFavoriteModelId
  ])

  // Handle API key change with debounced validation
  const handleKeyChange = useCallback((value, provider, setter, opts = { userInitiated: true }) => {
    // Update the key value in both state and ref
    setter(value)
    apiKeyRef.current[provider] = value

    // Only mark dirty when user typed
    if (opts.userInitiated) {
      setDirtyKeys(prev => ({ ...prev, [provider]: true }))
    }

    // Clear existing timer
    if (debounceTimers.current[provider]) {
      clearTimeout(debounceTimers.current[provider])
    }

    // Reset to idle if empty
    if (!value) {
      const nextStates = { ...validationStates, [provider]: 'idle' }
      setValidationStates(nextStates)
      setErrorMessages(prev => ({ ...prev, [provider]: '' }))
      setAvailableModels(prev => ({ ...prev, [provider]: 0 }))
      // Recompute preview when key is cleared (only if user initiated)
      if (opts.userInitiated) {
        setTimeout(() => recomputePreviewModels(nextStates), 0)
      }
      return
    }

    // Set validating state immediately
    setValidationStates(prev => ({ ...prev, [provider]: 'validating' }))

    // Set new debounce timer
    const newTimer = setTimeout(async () => {
      try {
        const result = await validateApiKey(value, provider)
        const newValidationState = result.is_valid ? 'valid' : 'invalid'

        setValidationStates(prev => ({
          ...prev,
          [provider]: newValidationState
        }))

        // Store or clear error message
        if (!result.is_valid && result.error_message) {
          setErrorMessages(prev => ({ ...prev, [provider]: result.error_message }))
        } else {
          setErrorMessages(prev => ({ ...prev, [provider]: '' }))
        }

        // Store available models count
        if (result.is_valid && result.available_models) {
          setAvailableModels(prev => ({ ...prev, [provider]: result.available_models.length }))
        } else {
          setAvailableModels(prev => ({ ...prev, [provider]: 0 }))
        }

        // Recompute preview after validation completes with updated state (only if user initiated)
        if (opts.userInitiated) {
          const updatedValidationStates = { ...validationStates, [provider]: newValidationState }
          await recomputePreviewModels(updatedValidationStates)
        }
      } catch (error) {
        setValidationStates(prev => ({ ...prev, [provider]: 'invalid' }))
        setErrorMessages(prev => ({ ...prev, [provider]: 'Validation failed' }))
        setAvailableModels(prev => ({ ...prev, [provider]: 0 }))

        // Recompute preview even on error (to remove invalidated models) - only if user initiated
        if (opts.userInitiated) {
          const updatedValidationStates = { ...validationStates, [provider]: 'invalid' }
          await recomputePreviewModels(updatedValidationStates)
        }
      }
    }, 500) // 500ms debounce

    debounceTimers.current[provider] = newTimer
  }, [validateApiKey, recomputePreviewModels, validationStates])

  const getTooltipText = (provider, status) => {
    if (status === 'idle') {
      let source = ''
      switch (provider) {
        case 'openai':
          source = 'Get your API key from platform.openai.com/api-keys.'
          break
        case 'anthropic':
          source = 'Get your API key from console.anthropic.com.'
          break
        case 'gemini':
          source = 'Get your API key from makersuite.google.com/app/apikey.'
          break
        case 'xai':
          source = 'Get your API key from console.x.ai.'
          break
        case 'openrouter':
          source = 'Get your API key from openrouter.ai/keys.'
          break
        case 'profile':
          source = 'This data is used to personalise future interactions. Data remains yours and is not used to build a profile outside of Consl.'
      }
      if (provider === 'profile') {
        return source;
      }

      return `API keys are encrypted and stored securely. Never exposed in browser storage.\n\n${source}`;
    } else if (status === 'validating') {
      return `Validating API key with ${provider.charAt(0).toUpperCase() + provider.slice(1)}`
    } else if (status === 'invalid') {
      if (errorMessages[provider]) {
        return errorMessages[provider]
      }
      return null
    } else if (status === 'valid') {
      const modelCount = availableModels[provider]
      if (modelCount > 0) {
        return `${modelCount} ${modelCount === 1 ? 'model' : 'models'} available`
      } else {
        return 'No models available for the configured API key'
      }
    } else {
      return null
    }
  }

  // Status indicator component
  const StatusIndicator = ({ tooltip }) => {
    return (
      <div className="relative group">
        <InfoIcon className="cursor-help size-4" />
        <div className="absolute right-0 bottom-full mb-spacing-2 w-[256px] p-spacing-2 bg-bg-tooltip text-[12px] text-primary rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 border border-border-default whitespace-pre-line">
          {tooltip ? tooltip : getTooltipText('profile', 'idle')}
        </div>
      </div>
    )
  }

  const handleDeleteAllConversations = async () => {
    await showConfirmation({
      title: 'Delete All Conversations',
      message: 'Are you sure you want to delete all conversations? This action cannot be undone and will permanently remove all of your chat history.',
      confirmText: 'Delete All Conversations',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        await deleteAllConversations();
        toast.success('All conversations deleted');
      }
    });
  };

  return (
    <PageLayout
      title="Settings"
      description="Manage your account preferences, API keys, and model settings."
    >

      {/* Preferences Section */}
      <section>
        <h2 className="text-[20px] font-normal mb-spacing-2">Preferences</h2>

        <div className="bg-bg-card rounded-lg border border-border-default">
          <SettingsRow label="First name" isFirst={true}>
            <div className="flex items-center gap-spacing-2">
              <SettingsInput
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={initializingUI}
              />
              <StatusIndicator />
            </div>
          </SettingsRow>

          <SettingsRow label="Last name">
            <div className="flex items-center gap-spacing-2">
              <SettingsInput
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={initializingUI}
              />
              <StatusIndicator />
            </div>
          </SettingsRow>

          <SettingsRow label="E-mail" isLast={true}>
            <div className="flex items-center gap-spacing-2">
              <SettingsInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your e-mail here"
                disabled={initializingUI}
              />
              <StatusIndicator />
            </div>
          </SettingsRow>
        </div>
      </section>

      {/* Model Preferences */}
      <ModelPreferences
        localEnabledModelIds={localEnabledModelIds}
        setLocalEnabledModelIds={setLocalEnabledModelIds}
        localFavoriteModelId={localFavoriteModelId}
        setLocalFavoriteModelId={setLocalFavoriteModelId}
        modelsOverride={previewModels && previewModels.length > 0 ? previewModels : undefined}
      />

      {/* Agent Tools */}
      <section className="mt-spacing-4">
        <h2 className="text-[20px] font-normal mb-spacing-2">Agent Tools</h2>

        <div className="bg-bg-card rounded-lg border border-border-default">
          {/* Working directory (Electron only) */}
          {isElectron && (
            <>
              <div className="flex flex-col gap-spacing-3 p-spacing-4 border-b border-border-divider">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-spacing-2">
                    <svg width="16" height="16" viewBox="0 0 16 16" strokeLinejoin="round" className="text-accent" style={{ color: 'currentColor' }}>
                      <path fillRule="evenodd" clipRule="evenodd" d="M14.5 7.5V12.5C14.5 13.0523 14.0523 13.5 13.5 13.5H2.5C1.94772 13.5 1.5 13.0523 1.5 12.5V7.5H14.5ZM14.5 6V4H8.83333C8.29241 4 7.76607 3.82456 7.33333 3.5L6 2.5H1.5V6H14.5ZM0 1H1.5H6.16667C6.38304 1 6.59357 1.07018 6.76667 1.2L8.23333 2.3C8.40643 2.42982 8.61696 2.5 8.83333 2.5H14.5H16V4V12.5C16 13.8807 14.8807 15 13.5 15H2.5C1.11929 15 0 13.8807 0 12.5V2.5V1Z" fill="currentColor" />
                    </svg>
                    <span className="text-dls-body text-primary font-medium">Working directory</span>
                  </div>
                  <div className="flex items-center gap-spacing-2">
                    {workingDirectory && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setWorkingDirectory('')
                          appStorage.removeItem('workingDirectory')
                        }}
                      >
                        Clear
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={async () => {
                        const dir = await window.electronAPI.selectDirectory()
                        if (dir) {
                          setWorkingDirectory(dir)
                          appStorage.setItem('workingDirectory', dir)
                          const toastId = toast.loading('Initializing workspace...')
                          try {
                            await window.electronAPI?.workspace?.init(dir)
                            toast.dismiss(toastId)
                          } catch {
                            toast.dismiss(toastId)
                            toast.error('Failed to initialize workspace')
                          }
                        }
                      }}
                    >
                      Browse
                    </Button>
                    <div className="relative group">
                      <InfoIcon className="cursor-help size-4 text-secondary hover:text-primary transition-colors" />
                      <div className="absolute right-0 bottom-full mb-spacing-2 w-[256px] p-spacing-2 bg-bg-tooltip text-[12px] text-primary rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 border border-border-default whitespace-pre-line">
                        Set the root folder for your workspace. Agent tools like file reading, editing, and search will operate within this directory.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-spacing-2 px-spacing-3 py-spacing-2 bg-bg-input rounded-md border border-border-default">
                  <svg width="14" height="14" viewBox="0 0 16 16" strokeLinejoin="round" className="flex-shrink-0 text-accent opacity-50">
                    <path fillRule="evenodd" clipRule="evenodd" d="M14.5 7.5V12.5C14.5 13.0523 14.0523 13.5 13.5 13.5H2.5C1.94772 13.5 1.5 13.0523 1.5 12.5V7.5H14.5ZM14.5 6V4H8.83333C8.29241 4 7.76607 3.82456 7.33333 3.5L6 2.5H1.5V6H14.5ZM0 1H1.5H6.16667C6.38304 1 6.59357 1.07018 6.76667 1.2L8.23333 2.3C8.40643 2.42982 8.61696 2.5 8.83333 2.5H14.5H16V4V12.5C16 13.8807 14.8807 15 13.5 15H2.5C1.11929 15 0 13.8807 0 12.5V2.5V1Z" fill="currentColor" />
                  </svg>
                  <span className="font-mono text-dls-body text-secondary truncate">
                    {workingDirectory || 'No directory selected'}
                  </span>
                </div>
              </div>

              {/* Filesystem tools status */}
              <div className="flex items-center justify-between px-spacing-4 py-spacing-3 border-b border-border-divider">
                <div className="flex items-center gap-spacing-2">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-secondary">
                    <path d="M4 2h5l3 3v9H4V2Z" stroke="currentColor" strokeWidth="1.3" fill="none"/>
                    <path d="M6 7h4M6 9.5h4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity="0.7"/>
                  </svg>
                  <span className="text-dls-body text-primary">Filesystem tools</span>
                </div>
                <div className="flex items-center gap-spacing-2">
                  {workingDirectory ? (
                    <>
                      <div className="w-2 h-2 rounded-full bg-success" />
                      <span className="text-dls-small text-success font-medium">Active</span>
                    </>
                  ) : (
                    <span className="text-dls-small text-secondary">Inactive</span>
                  )}
                  <div className="relative group">
                    <InfoIcon className="cursor-help size-4 text-secondary hover:text-primary transition-colors" />
                    <div className="absolute right-0 bottom-full mb-spacing-2 w-[256px] p-spacing-2 bg-bg-tooltip text-[12px] text-primary rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 border border-border-default whitespace-pre-line">
                      Filesystem tools are automatically enabled when a working directory is selected.
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Web search preference */}
          <div className="flex items-center justify-between px-spacing-4 py-spacing-3">
            <div className="flex items-center gap-spacing-2">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-secondary">
                <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M1.5 8h13M8 1.5c-2 2-2.5 4-2.5 6.5s.5 4.5 2.5 6.5M8 1.5c2 2 2.5 4 2.5 6.5s-.5 4.5-2.5 6.5" stroke="currentColor" strokeWidth="1.1"/>
              </svg>
              <span className="text-dls-body text-primary">Web search</span>
            </div>
            <div className="flex items-center gap-spacing-2">
              <div
                role="switch"
                aria-checked={preferAnthropicWebSearchLocal}
                aria-label="Web search"
                tabIndex={0}
                className={`relative w-9 h-5 rounded-full transition-colors ${!anthropicApiKey ? 'bg-bg-2 cursor-not-allowed' : preferAnthropicWebSearchLocal ? 'bg-accent cursor-pointer' : 'bg-bg-3 cursor-pointer'}`}
                onClick={() => { if (anthropicApiKey) setPreferAnthropicWebSearchLocal(!preferAnthropicWebSearchLocal) }}
                onKeyDown={(e) => { if ((e.key === ' ' || e.key === 'Enter') && anthropicApiKey) { e.preventDefault(); setPreferAnthropicWebSearchLocal(!preferAnthropicWebSearchLocal) } }}
              >
                <div className={`absolute top-[2px] left-[2px] w-4 h-4 ${!anthropicApiKey ? 'bg-secondary' : 'bg-white'} rounded-full transition-transform ${preferAnthropicWebSearchLocal ? 'translate-x-4' : ''}`} />
              </div>
              <div className="relative group">
                <InfoIcon className="cursor-help size-4 text-secondary hover:text-primary transition-colors" />
                <div className="absolute right-0 bottom-full mb-spacing-2 w-[256px] p-spacing-2 bg-bg-tooltip text-[12px] text-primary rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 border border-border-default whitespace-pre-line">
                  {!anthropicApiKey
                    ? "Requires Anthropic API key. When enabled, all models use Claude-based web search."
                    : "When enabled, all models use Claude-based web search. When disabled, models use their native capabilities."}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Configuration */}
      <section className="mt-spacing-4">
        <h2 className="text-[20px] font-normal mb-spacing-2">Configuration</h2>

        <div className="bg-bg-card rounded-lg border border-border-default">
          <SettingsRow label="OpenAI API key" isFirst={true}>
            <div className="flex items-center gap-spacing-2">
              <SettingsInput
                type="password"
                value={openAIApiKey}
                onChange={(e) => handleKeyChange(e.target.value, 'openai', setOpenAIApiKey)}
                placeholder="sk-..."
                disabled={initializingUI}
              />
              <ValidatorIcon status={initializingUI ? 'validating' : validationStates.openai} tooltip={getTooltipText('openai', validationStates.openai)} />
            </div>
          </SettingsRow>

          <SettingsRow label="Anthropic API key">
            <div className="flex items-center gap-spacing-2">
              <SettingsInput
                type="password"
                value={anthropicApiKey}
                onChange={(e) => handleKeyChange(e.target.value, 'anthropic', setAnthropicApiKey)}
                placeholder="sk-ant-..."
                disabled={initializingUI}
              />
              <ValidatorIcon status={initializingUI ? 'validating' : validationStates.anthropic} tooltip={getTooltipText('anthropic', validationStates.anthropic)} />
            </div>
          </SettingsRow>

          <SettingsRow label="Gemini API key">
            <div className="flex items-center gap-spacing-2">
              <SettingsInput
                type="password"
                value={geminiApiKey}
                onChange={(e) => handleKeyChange(e.target.value, 'gemini', setGeminiApiKey)}
                placeholder="AIza..."
                disabled={initializingUI}
              />
              <ValidatorIcon status={initializingUI ? 'validating' : validationStates.gemini} tooltip={getTooltipText('gemini', validationStates.gemini)} />
            </div>
          </SettingsRow>

          <SettingsRow label="xAI API key">
            <div className="flex items-center gap-spacing-2">
              <SettingsInput
                type="password"
                value={xaiApiKey}
                onChange={(e) => handleKeyChange(e.target.value, 'xai', setXaiApiKey)}
                placeholder="xai-..."
                disabled={initializingUI}
              />
              <ValidatorIcon status={initializingUI ? 'validating' : validationStates.xai} tooltip={getTooltipText('xai', validationStates.xai)} />
            </div>
          </SettingsRow>

          <SettingsRow label="OpenRouter API key">
            <div className="flex items-center gap-spacing-2">
              <SettingsInput
                type="password"
                value={openrouterApiKey}
                onChange={(e) => handleKeyChange(e.target.value, 'openrouter', setOpenrouterApiKey)}
                placeholder="sk-or-..."
                disabled={initializingUI}
              />
              <ValidatorIcon status={initializingUI ? 'validating' : validationStates.openrouter} tooltip={getTooltipText('openrouter', validationStates.openrouter)} />
            </div>
          </SettingsRow>

          <ActionRow
            label="Delete all conversations"
            tooltip="Permanently remove all your conversation history. This action cannot be undone."
            action={
              <Button
                variant="destructive"
                size="md"
                onClick={handleDeleteAllConversations}
              >
                Delete
              </Button>
            }
          />

          <ActionRow
            label="View onboarding"
            tooltip="Review the onboarding flow and customize your preferences."
            showSeparator
            action={
              <Button
                variant="secondary"
                size="md"
                onClick={() => navigate('/onboarding')}
              >
                View
              </Button>
            }
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end mt-spacing-4">
          {/* Check if any API key is invalid or being validated */}
          {(() => {
            const isValidating = Object.values(validationStates).some(state => state === 'validating')
            const hasInvalidKeys =
              (openAIApiKey && validationStates.openai === 'invalid') ||
              (anthropicApiKey && validationStates.anthropic === 'invalid') ||
              (geminiApiKey && validationStates.gemini === 'invalid') ||
              (xaiApiKey && validationStates.xai === 'invalid') ||
              (openrouterApiKey && validationStates.openrouter === 'invalid')
            const isDisabled = isValidating || hasInvalidKeys || initializingUI || savingProfile || savingApiKeys

            return (
              <div className="relative group">
                {isDisabled && (
                  <div className="absolute right-0 bottom-full mb-spacing-2 w-64 p-spacing-2 bg-bg-tooltip text-[12px] text-secondary rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 border border-border-default">
                    {isValidating ? 'Validating API keys' : initializingUI ? 'Loading your profile data and API keys' : 'Resolve your invalid API keys before saving'}
                  </div>
                )}
                <SaveBtn
                  isDisabled={isDisabled}
                  onClickFn={async () => {
                    if (isDisabled) return
                    setSavingProfile((prev) => true);
                    setSavingApiKeys((prev) => true);
                    try {
                      await Promise.all([
                        updateProfile(
                          { firstName, lastName, email }
                        ),
                        updateApiKeys(
                          { openAIApiKey, anthropicApiKey, geminiApiKey, xaiApiKey, openrouterApiKey }
                        ),
                        apiClient.put('/api/user-settings', {
                          enabled_model_ids: localEnabledModelIds,
                          favorite_model_id: localFavoriteModelId,
                          prefer_anthropic_web_search: preferAnthropicWebSearchLocal
                        })
                      ]);

                      // Update AppContext so changes reflect immediately
                      updateContextEnabledModelIds(localEnabledModelIds);
                      updateContextFavoriteModelId(localFavoriteModelId);
                      setPreferAnthropicWebSearch(preferAnthropicWebSearchLocal);

                      // Update initial values for model preferences
                      setInitialEnabledModelIds(localEnabledModelIds);
                      setInitialFavoriteModelId(localFavoriteModelId);

                      // Clear preview models (AppContext.models now has the updated list)
                      setPreviewModels(null);

                      toast.success('Settings saved successfully');
                    } catch (error) {
                      console.error(error)
                      toast.error('Failed to save settings');
                    } finally {
                      setSavingProfile(false);
                      setSavingApiKeys(false);
                    }
                  }}
                />
              </div>
            )
          })()}
        </div>
      </section>

    </PageLayout>
  )
}

export default SettingsPage
